/**
 * src/RegistryUtilitiesPage.js
 *
 * VISINVOKE Windows Knowledge Centre - landing page.
 *
 * A growing collection of Windows knowledge, tools, tweaks and technical
 * resources. Informational only. Selecting a category opens the dynamic route
 * /dashboard/utilities/registry/:categoryId.
 *
 * Visual refinement stage: naming, contrast, and hierarchy improved. Layout,
 * routing, and component structure are unchanged.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductNav from './components/ProductNav';
import { useAuth } from './AuthContext';
import API from './api';
import {
  RegistryHero,
  RegistryInformationPanel,
  RegistryCategoryCard,
  RegistrySection,
  hexA,
  rg,
} from './RegistryComponents';
import {
  REGISTRY_CATEGORIES,
  REGISTRY_CAPABILITIES,
  REGISTRY_BASE_ROUTE,
} from './registryConfig';

export default function RegistryUtilitiesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Technician Access uses the SAME backend-authoritative source as the Tech Hub
  // and the Manage Technician tab: GET /v1/technician-application/me ->
  // effective_capabilities.hub_access. That capability is already status-gated by
  // the backend (false unless the technician account is manually approved and
  // active), so no new flag is introduced here. Access defaults to locked while
  // loading and on any request failure.
  //
  // The Windows Knowledge Centre / Registry resources are technician resources.
  // Every signed-in user may BROWSE the catalogue (category names + documentation)
  // to see what approved technicians receive, but the operative registry content
  // is technician-only. There is currently no registry payload, download or asset
  // URL served anywhere in this collection (it is documentation only), so browsing
  // exposes no protected resource. When operative content is added it must run
  // through the Desktop Command Center under this same Technician Access gate.
  const [techApp, setTechApp] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!user) { setTechApp(null); return () => { cancelled = true; }; }
    API.get('/v1/technician-application/me')
      .then(r => { if (!cancelled) { setTechApp((r && r.data && r.data.application) ? r.data.application : null); } })
      .catch(() => { if (!cancelled) { setTechApp(null); } });
    return () => { cancelled = true; };
  }, [user]);

  const hasAccess = !!(
    techApp &&
    techApp.effective_capabilities &&
    techApp.effective_capabilities.hub_access === true
  );

  const openCategory = category => {
    navigate(`${REGISTRY_BASE_ROUTE}/${category.id}`);
  };

  return (
    <div style={page.root}>
      <ProductNav />

      {/* Back to Tech Utilities / Utilities */}
      <div style={page.backWrap}>
        <button
          style={page.backLink}
          onClick={() => navigate('/dashboard/utilities/tech-hub')}
          onMouseEnter={e => { e.currentTarget.style.color = '#fcd34d'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#aab8c9'; }}
        >
          {'←'} VISINVOKE Tech Utilities
        </button>
      </div>

      {/* Hero */}
      <RegistryHero
        eyebrow="VISINVOKE Tech Utilities"
        title="Windows Knowledge Centre"
        subtitle="A growing collection of Windows knowledge, tools, tweaks and technical resources. Built around safety, clear documentation, and complete undo support."
      />

      {/* Body */}
      <div style={page.body}>

        {/* Technician Access notice. The Windows Knowledge Centre / Registry
            resources are technician resources: everyone may browse the catalogue,
            but applying registry changes requires manually approved Technician
            Access and runs through the Desktop Command Center. */}
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 40px', width: '100%', boxSizing: 'border-box' }}>
          {hasAccess ? (
            <div style={{ ...page.notice, border: '1px solid ' + hexA('#34d399', 0.45), background: hexA('#34d399', 0.10) }}>
              <span style={page.noticeIcon}>{'\u{1F6E1}'}</span>
              <div>
                <div style={{ ...page.noticeTitle, color: '#34d399' }}>Technician Access active</div>
                <p style={page.noticeText}>
                  Your account has approved VISINVOKE Technician Access. Registry resources open and run through the
                  VISINVOKE Desktop Command Center. They are not applied from this web page.
                </p>
              </div>
            </div>
          ) : (
            <div style={{ ...page.notice, border: '1px solid ' + hexA('#fbbf24', 0.45), background: hexA('#fbbf24', 0.10) }}>
              <span style={page.noticeIcon}>{'\u{1F512}'}</span>
              <div>
                <div style={{ ...page.noticeTitle, color: '#fcd34d' }}>Technician Access required</div>
                <p style={page.noticeText}>
                  The Windows Knowledge Centre and its registry resources are technician resources. You can browse every
                  category below to see what approved VISINVOKE technicians receive. Applying registry changes requires
                  manually approved VISINVOKE Technician Access and runs through the Desktop Command Center &mdash; it is
                  never applied from this web page.
                </p>
                <button
                  style={page.noticeBtn}
                  onClick={() => navigate('/manage')}
                  onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
                >
                  Apply for Technician Access
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Capabilities panel */}
        <RegistrySection>
          <RegistryInformationPanel
            title="What the Windows Knowledge Centre provides"
            lead="Browse carefully documented Windows tweaks and resources, apply them safely, and undo them at any time. Every capability below is built into the Knowledge Centre framework."
            items={REGISTRY_CAPABILITIES}
            accent="#7dd3fc"
          />
        </RegistrySection>

        {/* Category grid */}
        <RegistrySection
          title="Knowledge Categories"
          description="Knowledge, tools and tweaks are organised into the categories below. Each category has its own focus. Select one to explore its entries as they are added."
        >
          <div style={{ ...rg.grid, marginTop: 18 }}>
            {REGISTRY_CATEGORIES.map(category => (
              <RegistryCategoryCard
                key={category.id}
                category={category}
                onOpen={openCategory}
              />
            ))}
          </div>
        </RegistrySection>

      </div>
    </div>
  );
}

const page = {
  root: {
    minHeight: '100vh',
    background: '#0d1117',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  body: {
    padding: '36px 0 72px',
    display: 'flex',
    flexDirection: 'column',
    gap: 40,
  },
  backWrap: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '22px 40px 0',
    width: '100%',
    boxSizing: 'border-box',
  },
  backLink: {
    background: 'transparent',
    border: 'none',
    color: '#aab8c9',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    padding: 0,
    transition: 'color 180ms ease',
  },
  notice: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    padding: '18px 20px',
    borderRadius: 14,
    boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
  },
  noticeIcon: {
    fontSize: 22,
    lineHeight: 1.2,
    flexShrink: 0,
  },
  noticeTitle: {
    fontSize: 15,
    fontWeight: 800,
    marginBottom: 6,
  },
  noticeText: {
    fontSize: 14,
    color: '#e2e8f0',
    lineHeight: 1.65,
    margin: 0,
    maxWidth: 900,
  },
  noticeBtn: {
    marginTop: 14,
    border: 'none',
    borderRadius: 10,
    padding: '10px 18px',
    fontSize: 14,
    fontWeight: 800,
    color: '#04121f',
    background: '#fbbf24',
    cursor: 'pointer',
    transition: 'filter 160ms ease',
  },
};
